import React from "react";

import { FilterAlt } from "@mui/icons-material";
import { isEqual } from "lodash-es";

import type { DeltaEnsembleIdent } from "@framework/DeltaEnsembleIdent";
import type { GuiEventPayloads } from "@framework/GuiMessageBroker";
import { GuiEvent, GuiState, useGuiState, useGuiValue } from "@framework/GuiMessageBroker";
import { Drawer } from "@framework/internal/components/Drawer";
import type { EnsembleRealizationFilterSelections } from "@framework/internal/components/EnsembleRealizationFilter";
import { EnsembleRealizationFilter } from "@framework/internal/components/EnsembleRealizationFilter";
import type { RegularEnsembleIdent } from "@framework/RegularEnsembleIdent";
import { UnsavedChangesAction } from "@framework/types/unsavedChangesAction";
import { areUnsortedArraysEqual } from "@framework/utils/arrayUtils";
import { getEnsembleIdentFromString } from "@framework/utils/ensembleIdentUtils";
import { countTrueValues } from "@framework/utils/objectUtils";
import { areParameterIdentStringToValueSelectionMapCandidatesEqual } from "@framework/utils/realizationFilterTypesUtils";
import type { Workbench } from "@framework/Workbench";
import { WorkbenchSessionTopic } from "@framework/WorkbenchSession";
import { usePublishSubscribeTopicValue } from "@lib/utils/PublishSubscribeDelegate";

import { useActiveSession } from "../../ActiveSessionBoundary";

export type RealizationFilterSettingsProps = { workbench: Workbench; onClose: () => void };

export const RealizationFilterSettings = React.memo(function RealizationFilterSettings(
    props: RealizationFilterSettingsProps,
) {
    const guiMessageBroker = props.workbench.getGuiMessageBroker();
    const rightSettingsPanelWidth = useGuiValue(guiMessageBroker, GuiState.RightSettingsPanelWidthInPercent);
    const ensembleSet = usePublishSubscribeTopicValue(
        props.workbench.getSessionManager().getActiveSession(),
        WorkbenchSessionTopic.ENSEMBLE_SET,
    );

    // Actually, this should subscribe to active workbench session change as well or use the `useActiveSession` hook,
    // but for now we assume that
    const session = useActiveSession();
    const realizationFilterSet = session.getRealizationFilterSet();

    const [numberOfUnsavedRealizationFiltersGuiState, setNumberOfUnsavedRealizationFiltersGuiState] = useGuiState(
        guiMessageBroker,
        GuiState.NumberOfUnsavedRealizationFilters,
    );
    const [numberOfEffectiveRealizationFiltersGuiState, setNumberOfEffectiveRealizationFiltersGuiState] = useGuiState(
        guiMessageBroker,
        GuiState.NumberOfEffectiveRealizationFilters,
    );

    const [numberOfUnsavedRealizationFilters, setNumberOfUnsavedRealizationFilters] = React.useState(
        numberOfUnsavedRealizationFiltersGuiState,
    );
    const [numberOfEffectiveRealizationFilters, setNumberOfEffectiveRealizationFilters] = React.useState(
        numberOfEffectiveRealizationFiltersGuiState,
    );

    const [activeFilterEnsembleIdent, setActiveFilterEnsembleIdent] = React.useState<
        RegularEnsembleIdent | DeltaEnsembleIdent | null
    >(null);

    // Maps for keeping track of unsaved changes and filter selections
    const [ensembleIdentStringHasUnsavedChangesMap, setEnsembleIdentStringHasUnsavedChangesMap] = React.useState<{
        [ensembleIdentString: string]: boolean;
    }>({});
    const [
        ensembleIdentStringToRealizationFilterSelectionsMap,
        setEnsembleIdentStringToRealizationFilterSelectionsMap,
    ] = React.useState<{
        [ensembleIdentString: string]: EnsembleRealizationFilterSelections;
    }>({});

    React.useEffect(
        function propagateNumberOfUnsavedRealizationFiltersGuiState() {
            if (numberOfUnsavedRealizationFilters !== numberOfUnsavedRealizationFiltersGuiState) {
                setNumberOfUnsavedRealizationFiltersGuiState(numberOfUnsavedRealizationFilters);
            }
        },
        [
            numberOfUnsavedRealizationFilters,
            numberOfUnsavedRealizationFiltersGuiState,
            setNumberOfUnsavedRealizationFiltersGuiState,
        ],
    );

    React.useEffect(
        function propagateNumberOfEffectiveRealizationFiltersGuiState() {
            if (numberOfEffectiveRealizationFilters !== numberOfEffectiveRealizationFiltersGuiState) {
                setNumberOfEffectiveRealizationFiltersGuiState(numberOfEffectiveRealizationFilters);
            }
        },
        [
            numberOfEffectiveRealizationFilters,
            numberOfEffectiveRealizationFiltersGuiState,
            setNumberOfEffectiveRealizationFiltersGuiState,
        ],
    );

    // Set no active filter if the settings panel is closed
    if (rightSettingsPanelWidth < 5 && activeFilterEnsembleIdent !== null) {
        setActiveFilterEnsembleIdent(null);
    }

    // Count ensembles where realizations are being filtered out
    const countEffectiveFilters = React.useCallback(
        function countEffectiveFilters() {
            return ensembleSet.getEnsembleArray().filter((ens) => {
                const filter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ens.getIdent());
                return !isEqual(filter.getFilteredRealizations().toSorted(), ens.getRealizations().toSorted());
            }).length;
        },
        [ensembleSet, realizationFilterSet],
    );

    // Create new maps if ensembles are added or removed
    const ensembleIdentStrings = ensembleSet.getEnsembleArray().map((ensemble) => ensemble.getIdent().toString());
    if (
        !areUnsortedArraysEqual(ensembleIdentStrings, Object.keys(ensembleIdentStringToRealizationFilterSelectionsMap))
    ) {
        // Create new maps with the new ensemble ident strings
        const updatedHasUnsavedChangesMap: { [ensembleIdentString: string]: boolean } = {
            ...ensembleIdentStringHasUnsavedChangesMap,
        };
        const updatedSelectionsMap: { [ensembleIdentString: string]: EnsembleRealizationFilterSelections } = {
            ...ensembleIdentStringToRealizationFilterSelectionsMap,
        };

        // Delete non-existing ensemble ident strings
        for (const ensembleIdentString of Object.keys(ensembleIdentStringToRealizationFilterSelectionsMap)) {
            if (!ensembleIdentStrings.includes(ensembleIdentString)) {
                delete updatedHasUnsavedChangesMap[ensembleIdentString];
                delete updatedSelectionsMap[ensembleIdentString];
            }
        }

        for (const ensembleIdentString of ensembleIdentStrings) {
            if (ensembleIdentString in updatedSelectionsMap) {
                // Skip if already exists
                continue;
            }

            const ensembleIdent = getEnsembleIdentFromString(ensembleIdentString);
            if (!ensembleIdent) {
                throw new Error(`Invalid ensemble ident: ${ensembleIdentString}`);
            }

            const realizationFilter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ensembleIdent);

            updatedHasUnsavedChangesMap[ensembleIdentString] = false;
            updatedSelectionsMap[ensembleIdentString] = {
                realizationNumbers: realizationFilter.getFilteredRealizations(),
                realizationNumberSelections: realizationFilter.getRealizationNumberSelections(),
                parameterIdentStringToValueSelectionReadonlyMap:
                    realizationFilter.getParameterIdentStringToValueSelectionReadonlyMap(),
                filterType: realizationFilter.getFilterType(),
                includeOrExcludeFilter: realizationFilter.getIncludeOrExcludeFilter(),
            };
        }
        setEnsembleIdentStringHasUnsavedChangesMap(updatedHasUnsavedChangesMap);
        setEnsembleIdentStringToRealizationFilterSelectionsMap(updatedSelectionsMap);
        setNumberOfUnsavedRealizationFilters(countTrueValues(updatedHasUnsavedChangesMap));
        setNumberOfEffectiveRealizationFilters(countEffectiveFilters());
    }

    const handleApplyAllClick = React.useCallback(
        function handleApplyAllClick() {
            // Apply all the unsaved changes state and reset the unsaved changes state
            const resetHasUnsavedChangesMap: { [ensembleIdentString: string]: boolean } = {};
            for (const ensembleIdentString in ensembleIdentStringToRealizationFilterSelectionsMap) {
                const ensembleIdent = getEnsembleIdentFromString(ensembleIdentString);
                if (!ensembleIdent) {
                    throw new Error(`Invalid ensemble ident: ${ensembleIdentString}`);
                }

                const realizationFilter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ensembleIdent);
                const selections = ensembleIdentStringToRealizationFilterSelectionsMap[ensembleIdent.toString()];

                // Apply the filter changes
                realizationFilter.setFilterType(selections.filterType);
                realizationFilter.setIncludeOrExcludeFilter(selections.includeOrExcludeFilter);
                realizationFilter.setRealizationNumberSelections(selections.realizationNumberSelections);
                realizationFilter.setParameterIdentStringToValueSelectionReadonlyMap(
                    selections.parameterIdentStringToValueSelectionReadonlyMap,
                );

                // Run filtering
                realizationFilter.runFiltering();

                // Reset the unsaved changes state
                resetHasUnsavedChangesMap[ensembleIdentString] = false;
            }

            setEnsembleIdentStringHasUnsavedChangesMap(resetHasUnsavedChangesMap);
            setNumberOfUnsavedRealizationFilters(0);
            setNumberOfUnsavedRealizationFiltersGuiState(0);

            const effectiveCount = countEffectiveFilters();
            setNumberOfEffectiveRealizationFilters(effectiveCount);
            setNumberOfEffectiveRealizationFiltersGuiState(effectiveCount);

            // Notify subscribers of change.
            props.workbench.getSessionManager().getActiveSession().notifyAboutEnsembleRealizationFilterChange();
        },
        [
            ensembleIdentStringToRealizationFilterSelectionsMap,
            realizationFilterSet,
            setNumberOfUnsavedRealizationFilters,
            setNumberOfUnsavedRealizationFiltersGuiState,
            setNumberOfEffectiveRealizationFilters,
            setNumberOfEffectiveRealizationFiltersGuiState,
            countEffectiveFilters,
            props.workbench,
        ],
    );

    const handleDiscardAllClick = React.useCallback(
        function handleDiscardAllClick() {
            // Discard all filter changes - i.e. reset the unsaved changes state
            const resetSelectionsMap: { [ensembleIdentString: string]: EnsembleRealizationFilterSelections } = {};
            const resetHasUnsavedChangesMap: { [ensembleIdentString: string]: boolean } = {};
            for (const ensembleIdentString in ensembleIdentStringToRealizationFilterSelectionsMap) {
                const ensembleIdent = getEnsembleIdentFromString(ensembleIdentString);
                if (!ensembleIdent) {
                    throw new Error(`Invalid ensemble ident: ${ensembleIdentString}`);
                }

                const realizationFilter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ensembleIdent);

                resetSelectionsMap[ensembleIdentString] = {
                    realizationNumbers: realizationFilter.getFilteredRealizations(),
                    realizationNumberSelections: realizationFilter.getRealizationNumberSelections(),
                    parameterIdentStringToValueSelectionReadonlyMap:
                        realizationFilter.getParameterIdentStringToValueSelectionReadonlyMap(),
                    filterType: realizationFilter.getFilterType(),
                    includeOrExcludeFilter: realizationFilter.getIncludeOrExcludeFilter(),
                };
                resetHasUnsavedChangesMap[ensembleIdentString] = false;
            }

            setEnsembleIdentStringToRealizationFilterSelectionsMap(resetSelectionsMap);
            setEnsembleIdentStringHasUnsavedChangesMap(resetHasUnsavedChangesMap);
            setNumberOfUnsavedRealizationFilters(0);
            setNumberOfUnsavedRealizationFiltersGuiState(0);

            const effectiveCount = countEffectiveFilters();
            setNumberOfEffectiveRealizationFilters(effectiveCount);
            setNumberOfEffectiveRealizationFiltersGuiState(effectiveCount);
        },
        [
            ensembleIdentStringToRealizationFilterSelectionsMap,
            realizationFilterSet,
            setNumberOfUnsavedRealizationFilters,
            setNumberOfUnsavedRealizationFiltersGuiState,
            setNumberOfEffectiveRealizationFilters,
            setNumberOfEffectiveRealizationFiltersGuiState,
            countEffectiveFilters,
        ],
    );

    React.useEffect(() => {
        function handleUnsavedChangesAction(
            payload: GuiEventPayloads[GuiEvent.UnsavedRealizationFilterSettingsAction],
        ) {
            if (payload.action === UnsavedChangesAction.Save) {
                handleApplyAllClick();
                setActiveFilterEnsembleIdent(null);
            } else if (payload.action === UnsavedChangesAction.Discard) {
                handleDiscardAllClick();
                setActiveFilterEnsembleIdent(null);
            }
        }

        const removeUnsavedChangesActionHandler = guiMessageBroker.subscribeToEvent(
            GuiEvent.UnsavedRealizationFilterSettingsAction,
            handleUnsavedChangesAction,
        );

        return () => {
            removeUnsavedChangesActionHandler();
        };
    }, [guiMessageBroker, handleApplyAllClick, handleDiscardAllClick]);

    function handleFilterSettingsClose() {
        props.onClose();
    }

    function handleApplyClick(ensembleIdent: RegularEnsembleIdent | DeltaEnsembleIdent) {
        const ensembleIdentString = ensembleIdent.toString();
        const realizationFilter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ensembleIdent);
        const selections = ensembleIdentStringToRealizationFilterSelectionsMap[ensembleIdentString];

        // Apply the filter changes
        realizationFilter.setFilterType(selections.filterType);
        realizationFilter.setIncludeOrExcludeFilter(selections.includeOrExcludeFilter);
        realizationFilter.setRealizationNumberSelections(selections.realizationNumberSelections);
        realizationFilter.setParameterIdentStringToValueSelectionReadonlyMap(
            selections.parameterIdentStringToValueSelectionReadonlyMap,
        );

        // Run filtering
        realizationFilter.runFiltering();

        // Reset the unsaved changes state
        const newHasUnsavedChangesMap = { ...ensembleIdentStringHasUnsavedChangesMap, [ensembleIdentString]: false };
        setEnsembleIdentStringHasUnsavedChangesMap(newHasUnsavedChangesMap);
        setNumberOfUnsavedRealizationFilters(countTrueValues(newHasUnsavedChangesMap));
        setNumberOfEffectiveRealizationFilters(countEffectiveFilters());

        // Notify subscribers of change.
        props.workbench.getSessionManager().getActiveSession().notifyAboutEnsembleRealizationFilterChange();
    }

    function handleDiscardClick(ensembleIdent: RegularEnsembleIdent | DeltaEnsembleIdent) {
        const ensembleIdentString = ensembleIdent.toString();
        const realizationFilter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ensembleIdent);
        setEnsembleIdentStringToRealizationFilterSelectionsMap({
            ...ensembleIdentStringToRealizationFilterSelectionsMap,
            [ensembleIdentString]: {
                realizationNumbers: realizationFilter.getFilteredRealizations(),
                realizationNumberSelections: realizationFilter.getRealizationNumberSelections(),
                parameterIdentStringToValueSelectionReadonlyMap:
                    realizationFilter.getParameterIdentStringToValueSelectionReadonlyMap(),
                filterType: realizationFilter.getFilterType(),
                includeOrExcludeFilter: realizationFilter.getIncludeOrExcludeFilter(),
            },
        });

        // Reset the unsaved changes state
        const newHasUnsavedChangesMap = { ...ensembleIdentStringHasUnsavedChangesMap, [ensembleIdentString]: false };
        setEnsembleIdentStringHasUnsavedChangesMap(newHasUnsavedChangesMap);
        setNumberOfUnsavedRealizationFilters(countTrueValues(newHasUnsavedChangesMap));
        setNumberOfEffectiveRealizationFilters(countEffectiveFilters());
    }

    function handleFilterChange(
        ensembleIdent: RegularEnsembleIdent | DeltaEnsembleIdent,
        selections: EnsembleRealizationFilterSelections,
    ) {
        const ensembleIdentString = ensembleIdent.toString();

        // Register the filter changes in the map
        setEnsembleIdentStringToRealizationFilterSelectionsMap({
            ...ensembleIdentStringToRealizationFilterSelectionsMap,
            [ensembleIdentString]: selections,
        });

        // Check if the filter changes are different from the original filter
        const realizationFilter = realizationFilterSet.getRealizationFilterForEnsembleIdent(ensembleIdent);
        const hasUnsavedChanges =
            !isEqual(selections.realizationNumberSelections, realizationFilter.getRealizationNumberSelections()) ||
            !areParameterIdentStringToValueSelectionMapCandidatesEqual(
                selections.parameterIdentStringToValueSelectionReadonlyMap,
                realizationFilter.getParameterIdentStringToValueSelectionReadonlyMap(),
            ) ||
            selections.filterType !== realizationFilter.getFilterType() ||
            selections.includeOrExcludeFilter !== realizationFilter.getIncludeOrExcludeFilter();

        // Update the unsaved changes state
        const newHasUnsavedChangesMap = {
            ...ensembleIdentStringHasUnsavedChangesMap,
            [ensembleIdentString]: hasUnsavedChanges,
        };
        setEnsembleIdentStringHasUnsavedChangesMap(newHasUnsavedChangesMap);
        setNumberOfUnsavedRealizationFilters(countTrueValues(newHasUnsavedChangesMap));
        setNumberOfEffectiveRealizationFilters(countEffectiveFilters());
    }

    function handleSetActiveEnsembleRealizationFilter(ensembleIdent: RegularEnsembleIdent | DeltaEnsembleIdent) {
        setActiveFilterEnsembleIdent(ensembleIdent);
    }

    function handleOnEnsembleRealizationFilterHeaderClick(ensembleIdent: RegularEnsembleIdent | DeltaEnsembleIdent) {
        if (activeFilterEnsembleIdent?.equals(ensembleIdent)) {
            setActiveFilterEnsembleIdent(null);
        }
    }

    function createEnsembleRealizationFilterList() {
        if (!ensembleSet.hasAnyEnsembles()) {
            return (
                <div className="px-xs py-xs text-neutral-subtle flex h-full flex-col items-center justify-center text-center">
                    No ensembles available. Please add ensembles to the session to enable realization filtering.
                </div>
            );
        }

        return (
            <div className="px-xs py-2xs gap-y-xs flex flex-col overflow-y-auto">
                {ensembleSet.getEnsembleArray().map((ensemble) => {
                    const ensembleIdent = ensemble.getIdent();
                    const isActive =
                        activeFilterEnsembleIdent !== null && activeFilterEnsembleIdent.equals(ensembleIdent);
                    const isAnotherActive = !isActive && activeFilterEnsembleIdent !== null;

                    const selections = ensembleIdentStringToRealizationFilterSelectionsMap[ensembleIdent.toString()];
                    const currentFilteredRealizations = realizationFilterSet
                        .getRealizationFilterForEnsembleIdent(ensembleIdent)
                        .getFilteredRealizations();

                    if (!selections) {
                        return null;
                    }
                    return (
                        <EnsembleRealizationFilter
                            key={ensembleIdent.toString()}
                            ensemble={ensemble}
                            filteredRealizationNumbers={currentFilteredRealizations}
                            selections={selections}
                            hasUnsavedSelections={ensembleIdentStringHasUnsavedChangesMap[ensembleIdent.toString()]}
                            isActive={isActive}
                            isAnotherFilterActive={isAnotherActive}
                            onClick={() => handleSetActiveEnsembleRealizationFilter(ensembleIdent)}
                            onHeaderClick={() => handleOnEnsembleRealizationFilterHeaderClick(ensembleIdent)}
                            onFilterChange={(newSelections) => handleFilterChange(ensembleIdent, newSelections)}
                            onApplyClick={() => handleApplyClick(ensembleIdent)}
                            onDiscardClick={() => handleDiscardClick(ensembleIdent)}
                        />
                    );
                })}
            </div>
        );
    }

    return (
        <div className="h-full w-full">
            <Drawer
                title="Realization Filter"
                icon={<FilterAlt />}
                visible={true}
                onClose={handleFilterSettingsClose}
            >
                {createEnsembleRealizationFilterList()}
            </Drawer>
        </div>
    );
});
